import React, { PropsWithChildren } from 'react';

import { PolicyItemGroup, PolicyUnion } from '../../types';

import styles from './PoliciesList.pcss';
import { PolicyTitle } from './PolicyTitle';

export interface PoliciesListProps {
  readonly policies: Array<PolicyUnion>;
  readonly relatedInputFocused: boolean;
  readonly currentErrorKeys: Set<string>;
}

/**
 * @returns true when following prev/next props are equal:
 */
export const propsAreEqual = (
  prevProps: Readonly<PropsWithChildren<PoliciesListProps>>,
  nextProps: Readonly<PropsWithChildren<PoliciesListProps>>,
): boolean => (
  // policies equality
  prevProps.policies === nextProps.policies
  // input focusing equality
  && prevProps.relatedInputFocused === nextProps.relatedInputFocused
  // current error keys equality
  && JSON.stringify(
    Array.from(prevProps.currentErrorKeys),
  ) === JSON.stringify(
    Array.from(nextProps.currentErrorKeys),
  )
);

/**
 * - Renders passed policies in a list
 * - Paints policies in red if they have errors
 *
 * This component is memoized according to its multiply rendering.
 * Usually, the policy list is dynamic and responsive to some value from the input field.
 */
export const PoliciesList: React.FC<PoliciesListProps> = React.memo(({
  relatedInputFocused,
  currentErrorKeys,
  policies,
}) => {
  const isPolicyGroup = (policy: PolicyUnion) => (
    Array.isArray((policy as PolicyItemGroup).policies)
  );

  const isViolatedPolicy = (policy: PolicyUnion, errorKeys: Set<string>) => (
    errorKeys.has(policy.validationRule)
  );

  const renderTitle = (policy: PolicyUnion, isViolated: boolean) => (
    <PolicyTitle
      title={policy.title}
      status={
        // paint a policy in red, if a related validation rule was not passed
        isViolated ? 'invalid' : 'default'
      }
    />
  );

  // do not show the list when:
  if (
    // no defined policies
    policies.length <= 0
    // related input is not focused AND it doesn't have error/s
    || (!relatedInputFocused && !currentErrorKeys.size)) {
    return null;
  }

  return (
    <ul className={styles.policyList} data-stable-name="PoliciesList">
      {policies.map((policy) => {
        const isViolated = isViolatedPolicy(policy, currentErrorKeys);

        return (
          <li
            key={policy.title}
            className={isPolicyGroup(policy) ? styles.policyGroupHeading : ''}
            data-stable-name="PoliciesListItem"
            data-invalid={isViolated}
          >
            {
              isPolicyGroup(policy)
                ? (
                  <>
                    {renderTitle(policy, isViolated)}
                    <ul className={styles.policyGroupList}>
                      {(policy as PolicyItemGroup).policies?.map((subPolicy) => {
                        const isSubViolated = isViolatedPolicy(subPolicy, currentErrorKeys);

                        return (
                          <li
                            key={subPolicy.title}
                            data-stable-name="PolicySubListItem"
                            data-invalid={isSubViolated}
                          >
                            {renderTitle(subPolicy, isSubViolated)}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )
                : renderTitle(policy, isViolated)
            }
          </li>
        )
      })}
    </ul>
  );
}, propsAreEqual);
